import { css } from "@emotion/react";
import React, { Suspense, lazy, useEffect } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import {
  Route,
  BrowserRouter as Router,
  Switch,
  useLocation,
} from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  ampPriceAtom,
  fxcBalanceAtom,
  migrateModalSeenAtom,
} from "../../atoms";
import { Flex } from "../../base";
import { useFirebase, useTopAddresses } from "../../modules/firebase";
import {
  useBalanceUpdater,
  useBeenHereBefore,
  useSuppressMigrateModal,
} from "../../modules/hooks";
import AccountUpdater from "../AccountUpdater";
import AppsUpdater from "../AppsUpdater";
import MetaMaskHUD from "../MetaMaskHUD";
import MigrateModal from "../MigrateModal";
import NotificationHUD from "../NotificationHUD";
import Page from "../Page";
import SineWave from "../SineWave";
import BannedRedirect from "./BannedRedirect";

const ConnectModal = lazy(() => import("../ConnectModal"));
const BadNetworkModal = lazy(() => import("../BadNetworkModal"));
const MigrateRoute = lazy(() => import("./MigrateRoute"));
const MainRoute = lazy(() => import("./MainRoute"));

const ShowMigrateModal = () => {
  const [suppressMigrateModal] = useSuppressMigrateModal();
  const migrateModalSeen = useRecoilValue(migrateModalSeenAtom);
  const fxcBalance = useRecoilValue(fxcBalanceAtom);

  const [beenHereBefore] = useBeenHereBefore();

  const { pathname } = useLocation();
  const migrating = pathname.includes("/migrate");

  const show =
    fxcBalance?.gt(0) &&
    beenHereBefore &&
    !migrating &&
    !migrateModalSeen &&
    !suppressMigrateModal;

  return show ? <MigrateModal /> : null;
};

const useAmpPriceUpdater = () => {
  const app = useFirebase();
  const setAmpPrice = useSetRecoilState(ampPriceAtom);
  const [ampPriceQuote, loading, error] = useDocumentData<any>(
    app?.firestore().doc("prices/amp/quoteAssets/usd")
  );

  useEffect(() => {
    if (!loading && !error && ampPriceQuote?.price) {
      setAmpPrice(Number(ampPriceQuote.price));
    }
  }, [loading, error, ampPriceQuote, setAmpPrice]);
};

const AmpUpdater = () => {
  useAmpPriceUpdater();
  useTopAddresses();
  useBalanceUpdater();
  return null;
};

const AppRouter = () => (
  <Router>
    <Page>
      <React.Suspense fallback={null}>
        <BannedRedirect />
      </React.Suspense>

      <React.Suspense fallback={null}>
        <AmpUpdater />
        <AccountUpdater />
        <AppsUpdater />
      </React.Suspense>

      <MetaMaskHUD />
      <NotificationHUD />

      <React.Suspense fallback={null}>
        <ShowMigrateModal />
      </React.Suspense>

      <Switch>
        <Route
          path="/connect"
          render={() => (
            <Suspense>
              <ConnectModal />
            </Suspense>
          )}
        />
        <Route path="/">
          <Suspense>
            <BadNetworkModal />
          </Suspense>
        </Route>
      </Switch>

      <Switch>
        <Route path="/migrate">
          <Suspense>
            <MigrateRoute />
          </Suspense>
        </Route>

        <Route path="/">
          {/* <AnimatePresence mode="wait"> */}
          <React.Suspense
            key="wave"
            fallback={
              <Flex
                center
                css={css`
                  padding-top: 10rem;
                  width: 100%;
                  height: 500px;
                `}
              >
                <SineWave />
              </Flex>
            }
          >
            <MainRoute key="mainRoute" />
          </React.Suspense>
          {/* </AnimatePresence> */}
        </Route>
      </Switch>
    </Page>
  </Router>
);

export default AppRouter;
